import {
  HomeIcon,
  UserMinusIcon,
  UserPlusIcon,
} from "@heroicons/react/20/solid";
import LessonViewer from "../../components/scheduler/LessonViewer";
import Card from "../../components/ui-lib/Card";
import { ViewModeFullWeek } from "../../components/ui-lib/scheduler/viewModeFullWeek";
import VacancyStatus from "../../components/vacancy/VacancyStatus";
import { generateComponentSchedule } from "../../helpers/utils/calendar.util";
import { Vacancy } from "../../models/vacancy/vacancy";
import dayjs from "dayjs";
import { useRef } from "react";
import useRefSizeObserver from "../../hooks/useRefSizeObserver";

type VacancyCardProps = {
  vacancy: Vacancy;
  onClick: () => void;
};

export function VacancyCard({ vacancy, onClick }: VacancyCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  const width = useRefSizeObserver(cardRef?.current);

  return (
    <Card
      className="duration-300 hover:cursor-pointer hover:shadow-md"
      ref={cardRef}
      onClick={onClick}
    >
      <VacancyStatus vacancy={vacancy}></VacancyStatus>
      <LessonViewer
        lessons={generateComponentSchedule(
          vacancy.schedule,
          new Date(vacancy.startDate),
          new Date(vacancy.endDate),
          vacancy.lessons,
        )}
        startDate={new Date(vacancy.startDate)}
        viewMode={new ViewModeFullWeek(true)}
      ></LessonViewer>
      <span className="ml-4 flex flex-col text-left font-bold">
        <span>{`${dayjs(vacancy.startDate).format("dddd, L")}`}</span>
        <span className="font-normal">bis</span>
        <span>{`${dayjs(vacancy.endDate).format("dddd, L")}`}</span>
      </span>
      {width >= 600 && (
        <span className="mx-auto flex items-center font-bold">
          <HomeIcon className="mr-2 h-5 w-5 text-th-primary-900" />
          {vacancy.subSchools.map((s) => s.name).join(", ")}
        </span>
      )}
      {width >= 500 && (
        <div className="ml-auto">
          <div className="flex items-center space-x-2">
            <UserMinusIcon className="h-5 w-5 text-red-500" />
            <span className="text-left font-bold">
              {vacancy.absentTeacher.name} {vacancy.absentTeacher.lastname}
            </span>
          </div>
          {vacancy.teacherAccepted?.map((t) => (
            <div
              key={`${t.id}_vacancy_teacher`}
              className="flex items-center space-x-2"
            >
              <UserPlusIcon className="h-5 w-5 text-green-900" />
              <span className="text-left font-bold">
                {t.name} {t.lastname}
              </span>
            </div>
          ))}
        </div>
      )}
      {width < 500 && (
        <div
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-full bg-red-400 font-bold text-white"
          title={`${vacancy.absentTeacher.name} ${vacancy.absentTeacher.lastname}`}
        >
          {vacancy.absentTeacher.name[0]}
          {vacancy.absentTeacher.lastname[0]}
        </div>
      )}
    </Card>
  );
}

export function TeacherVacancyCard({ vacancy, onClick }: VacancyCardProps) {
  return (
    <div
      className="relative rounded-lg bg-white p-4 shadow-sm duration-300 hover:cursor-pointer hover:shadow-md"
      onClick={onClick}
    >
      {!!vacancy.teacherApplications?.length && (
        <div className="absolute right-0 top-0 -mr-2 -mt-2 h-6 w-6 rounded-full bg-green-900 text-center font-bold text-white">
          !
        </div>
      )}
      <h3>{`${vacancy.school.name} ${new Date(
        vacancy.startDate,
      ).toLocaleDateString()} - ${new Date(
        vacancy.endDate,
      ).toLocaleDateString()}`}</h3>
      <p>{vacancy.description}</p>
    </div>
  );
}
